// What each custom field type's filter control offers, and how its typed-in draft becomes a stored
// condition. A draft is incomplete (Add disabled, closing applies nothing), invalid (Add disabled with a
// reason) or a condition ready to apply. Numbers, a Currency amount among them (typed without its symbol), are
// read in the decimal separator the caller passes and stored with '.'.
import {
	compareCustomFieldSortValues,
	customFieldSortValue,
	type CustomFieldColumnDef,
	type CustomFieldKind,
} from '../custom-field-columns/customFieldColumnModel';
import type {ValueCondition} from './customFieldFilterConditions';
import {DATE_PRESETS, isDatePreset, type DatePreset} from './customFieldFilterDates';

export type ConditionOp = 'contains' | 'is' | 'equals' | 'gt' | 'lt' | 'between' | 'empty' | DatePreset | 'custom_range';

// The custom field types the filter panel can filter on. A type the lists can show but that has no filter
// control yet (Yes/No, Hyperlink) is left out of the panel rather than offered with no conditions.
export const FILTERABLE_CUSTOM_FIELD_KINDS = ['TEXT', 'DROPDOWN', 'INTEGER', 'DECIMAL', 'CURRENCY', 'DATE'] as const;
export type FilterableCustomFieldKind = (typeof FILTERABLE_CUSTOM_FIELD_KINDS)[number];

export const isFilterableCustomField = (def: CustomFieldColumnDef): boolean =>
	(FILTERABLE_CUSTOM_FIELD_KINDS as readonly CustomFieldKind[]).includes(def.kind);

export function conditionOpsFor(kind: CustomFieldKind): ConditionOp[] {
	switch (kind) {
		case 'TEXT':
			return ['contains', 'is', 'empty'];
		case 'INTEGER':
		case 'DECIMAL':
		case 'CURRENCY':
			return ['equals', 'between', 'gt', 'lt', 'empty'];
		case 'DATE':
			return [...DATE_PRESETS, 'custom_range', 'empty'];
		default:
			return [];
	}
}

/** The pill selected when nothing is applied yet. */
export function defaultOpFor(kind: CustomFieldKind): ConditionOp {
	return kind === 'INTEGER' || kind === 'DECIMAL' || kind === 'CURRENCY' ? 'between' : conditionOpsFor(kind)[0];
}

/** Whether the condition takes a typed value. Only a typed value is applied by closing without Add. */
export const needsInput = (op: ConditionOp): boolean => op !== 'empty' && !isDatePreset(op);

export interface ConditionDraft {
	value: string;
	from: string;
	to: string;
}

export const EMPTY_DRAFT: ConditionDraft = {value: '', from: '', to: ''};

export type DraftResult = {condition: ValueCondition} | {error: 'integer' | 'decimal' | 'range'} | {incomplete: true};

const INCOMPLETE: DraftResult = {incomplete: true};

// One number box: undefined when blank, an error when not a number of the field's kind.
const parseNumber = (
	kind: CustomFieldKind,
	text: string,
	decimalSeparator: ',' | '.'
): string | undefined | {error: 'integer' | 'decimal'} => {
	const trimmed = text.trim();
	if (trimmed === '') return undefined;
	if (kind === 'INTEGER') return /^-?\d+$/.test(trimmed) ? trimmed : {error: 'integer'};
	const normalised = decimalSeparator === ',' ? trimmed.replace(',', '.') : trimmed;
	return /^-?\d+(\.\d+)?$/.test(normalised) ? normalised : {error: 'decimal'};
};

/** Which format error a number box's text has, if any; blank text has none. */
export const numberFormatError = (
	kind: CustomFieldKind,
	text: string,
	decimalSeparator: ',' | '.'
): 'integer' | 'decimal' | undefined => {
	const parsed = parseNumber(kind, text, decimalSeparator);
	return typeof parsed === 'object' ? parsed.error : undefined;
};

const numberDef = (kind: CustomFieldKind): CustomFieldColumnDef => ({
	id: '',
	key: '',
	displayName: '',
	kind,
	readOnly: false,
	options: [],
});

const numberCondition = (
	kind: CustomFieldKind,
	op: ConditionOp,
	draft: ConditionDraft,
	decimalSeparator: ',' | '.'
): DraftResult => {
	if (op === 'between') {
		const from = parseNumber(kind, draft.from, decimalSeparator);
		const to = parseNumber(kind, draft.to, decimalSeparator);
		if (typeof from === 'object') return from;
		if (typeof to === 'object') return to;
		if (from === undefined || to === undefined) return INCOMPLETE;
		const def = numberDef(kind);
		const compare = compareCustomFieldSortValues(def);
		const order = compare(customFieldSortValue(def, from) as string, customFieldSortValue(def, to) as string);
		return order > 0 ? {error: 'range'} : {condition: {op: 'between', from, to}};
	}
	const value = parseNumber(kind, draft.value, decimalSeparator);
	if (typeof value === 'object') return value;
	if (value === undefined) return INCOMPLETE;
	return {condition: {op: op as 'equals' | 'gt' | 'lt', value}};
};

export function toCondition(
	kind: CustomFieldKind,
	op: ConditionOp,
	draft: ConditionDraft,
	decimalSeparator: ',' | '.'
): DraftResult {
	if (op === 'empty') return {condition: {op: 'empty'}};
	switch (kind) {
		case 'TEXT': {
			const value = draft.value.trim();
			return value === '' ? INCOMPLETE : {condition: {op: op as 'contains' | 'is', value}};
		}
		case 'INTEGER':
		case 'DECIMAL':
		case 'CURRENCY':
			return numberCondition(kind, op, draft, decimalSeparator);
		case 'DATE':
			if (isDatePreset(op)) return {condition: {op: 'preset', preset: op}};
			if (!draft.from || !draft.to) return INCOMPLETE;
			return draft.from > draft.to ? {error: 'range'} : {condition: {op: 'between', from: draft.from, to: draft.to}};
		default:
			return INCOMPLETE;
	}
}

/** The pill for an applied condition, or the type's default when nothing is applied. */
export function opOf(condition: ValueCondition | null, kind: CustomFieldKind): ConditionOp {
	if (!condition) return defaultOpFor(kind);
	if (condition.op === 'preset') return condition.preset;
	if (condition.op === 'between' && kind === 'DATE') return 'custom_range';
	return condition.op;
}

const sameTerm = (a: string, b: string): boolean => a.trim().toLowerCase() === b.trim().toLowerCase();

/**
 * The field's values after adding a condition. A number or date filter holds one condition. A text filter
 * holds terms that share one condition: a new term joins them, and a term with the other condition, or is
 * empty, replaces them.
 */
export function nextSelection(
	kind: CustomFieldKind,
	applied: readonly ValueCondition[],
	condition: ValueCondition
): ValueCondition[] {
	if (kind !== 'TEXT' || condition.op === 'empty' || !('value' in condition)) return [condition];
	const terms = applied.filter(entry => entry.op === condition.op && 'value' in entry) as {
		op: 'contains' | 'is';
		value: string;
	}[];
	return terms.some(term => sameTerm(term.value, condition.value)) ? terms : [...terms, condition];
}
